import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LngLatBoundsLike } from "react-map-gl/maplibre";
import { SelectAddressArgs } from "../step-config-file/types";
import { ButtonGroup } from "@/components/ui/button-group";

interface FormComponentProps {
  moveToNextLocation: (args: SelectAddressArgs) => void;
  setBasemap: (props: any) => void;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface LocationData {
  name: string;
  coordinates: Coordinates;
  bbox?: LngLatBoundsLike;
}

export const FormComponent: React.FC<FormComponentProps> = ({
  setBasemap,
  moveToNextLocation,
}) => {
  const locations: LocationData[] = [
    {
      name: "MARYVALE",
      coordinates: {
        latitude: -24.660497665405273,
        longitude: 134.01792907714844,
      },
      bbox: [133.2604, -25.3281, 134.7754, -23.9929],
    },
    {
      name: "PALTALLOCH",
      coordinates: {
        latitude: -35.509664552499956,
        longitude: 139.29260605849998,
      },
      bbox: [138.7621, -35.9459, 139.8231, -35.0735],
    },
    {
      name: "CLOVER DOWNS",
      coordinates: {
        latitude: -28.406380653381348,
        longitude: 146.11844635009766,
      },
      bbox: [145.4185, -29.0578, 146.8184, -27.7549],
    },
    {
      name: "WALGETT",
      coordinates: {
        latitude: -29.8221788820501,
        longitude: 148.13796637353249,
      },
      bbox: [147.5583, -30.3106, 148.7177, -29.3337],
    },
  ];

  const handleLocationSelect = (location: string) => {
    const selectedLocation = locations.find((loc) => loc.name === location);

    if (selectedLocation) {
      moveToNextLocation({
        ...selectedLocation.coordinates,
        geocoder_attributes: {},
        zoomLevel: 10,
        boundingBox: selectedLocation.bbox,
      });
    }
  };

  const handleBasemapChange = (basemap: string) => {
    setBasemap(basemap);
  };

  return (
    <AOISelector
      onLocationSelect={handleLocationSelect}
      onBasemapChange={handleBasemapChange}
    />
  );
};

interface AOISelectorProps {
  onLocationSelect?: (location: string) => void;
  onBasemapChange?: (basemap: string) => void;
}

const locations = ["WALGETT", "MARYVALE", "CLOVER DOWNS", "PALTALLOCH"];
const basemaps = ["Basemap", "Imagery"];

const AOISelector: React.FC<AOISelectorProps> = ({
  onLocationSelect,
  onBasemapChange,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedBasemap, setSelectedBasemap] = useState<string>(basemaps[0]);

  const handleLocationClick = (location: string) => {
    setSelectedLocation(location);
    onLocationSelect?.(location);
  };

  const handleBasemapChange = (value: string) => {
    setSelectedBasemap(value);
    onBasemapChange?.(value);
  };

  return (
    <Card className="w-40">
      <CardContent className="flex flex-col gap-2">
        <Label>AOI Selector</Label>

        <ButtonGroup orientation="vertical" className="w-full">
          {locations.map((location) => (
            <Button
              key={location}
              className={cn(
                "",
                selectedLocation === location ? "bg-primary/80" : "",
              )}
              onClick={() => handleLocationClick(location)}
            >
              {location}
            </Button>
          ))}
        </ButtonGroup>

        <div className="space-y-1.5">
          <Label>Basemap</Label>
          <Select value={selectedBasemap} onValueChange={handleBasemapChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {basemaps.map((basemap) => (
                <SelectItem key={basemap} value={basemap}>
                  {basemap}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
};
